import { Component, Fragment, h, Prop, State } from '@stencil/core';
import rentwareIcon from '../../../types/icons';
import { InputStyle } from '../../../types/types';
import { dateToString, triggerInputChange } from '../../../utils/utils';
import UploadIcon from '../svg-elements/upload-icon';

@Component({
  tag: 'rtr-input',
  styleUrl: 'style.sass',
  shadow: true,
})
export class RentwareInput {
  @Prop() locale: string;
  @Prop() inputStyle: InputStyle = InputStyle.Default;
  @Prop() type?: string = 'text';
  @Prop() name: string;
  @Prop() label?: string;
  @Prop() value: string;
  @Prop() placeholder?: string;
  @Prop() max?: string;
  @Prop() min?: string;
  @Prop() disabled?: boolean;
  @Prop() readOnly?: boolean;
  @Prop() required?: boolean;
  @Prop() wasTouched?: boolean;
  @Prop() onInput?: any;
  @Prop() onBlur?: any;
  @Prop() onFocus?: any;
  @Prop() inputRef?: any;
  @Prop() inputUnRef?: any;
  @Prop() error: boolean = false;
  @Prop() customIconName?: string;
  @Prop() customIconStyle?: string;
  @Prop() fixedStyle: boolean = false; // for preview

  @State() selectedDate: Date;

  inputInternalRef?: any;

  disconnectedCallback() {
    if (this.inputUnRef) {
      this.inputUnRef(this.inputInternalRef, this.name);
    }
  }

  get wrapperClass() {
    return {
      InputWrapperDefault: this.type !== 'file' && this.inputStyle === InputStyle.Default,
      InputWrapperDefaultWithFileUpload: this.type === 'file' && this.inputStyle === InputStyle.Default,
      InputWrapperConfig: this.inputStyle === InputStyle.Config || this.inputStyle === InputStyle.NewConfig,
      InputWrapperNewConfig: this.inputStyle === InputStyle.NewConfig,
      InputWrapperSimple: this.simpleGroup,
      InputWrapperOrder: this.inputStyle === InputStyle.OrderConfig,
      Error: this.error,
      Touched: this.wasTouched,
      FixedStyle: this.fixedStyle,
    };
  }

  get labelClass() {
    return {
      LabelError: this.error,
      Touched: this.wasTouched,
      FixedStyle: this.fixedStyle,
    };
  }

  handleDateClick = date => {
    this.selectedDate = date;
    triggerInputChange(this.inputInternalRef, dateToString(date));
  };

  renderDateInput() {
    return (
      <Fragment>
        <rentware-date-select
          onDayClick={this.handleDateClick}
          bookingDate={this.value ? new Date(this.value) : this.selectedDate}
          label={this.label}
          hasValidationError={this.error}
          locale={this.locale}
        />

        <input
          class={{ HiddenInput: true, FixedStyle: this.fixedStyle }}
          type={this.type}
          name={this.name}
          id={this.name}
          value={this.selectedDate ? dateToString(this.selectedDate) : this.value}
          disabled={this.disabled}
          required={this.required}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          onInput={this.onInput}
          ref={(el: HTMLInputElement) => {
            this.inputInternalRef = el;
            if (this.inputRef) {
              this.inputRef(el);
            }
          }}
        />
      </Fragment>
    );
  }

  renderFileInput() {
    const input = this.inputInternalRef;
    if (input && input.files && input.files.length > 0) {
      const file = input.files[0];
      this.value = file.name;
    } else {
      // because of how API works, we need to reset the value to empty string for now 🙈
      this.value = '';
    }

    return (
      <Fragment>
        <input
          type={this.type}
          name={this.name}
          id={this.name}
          class={{ HiddenInput: true, FixedStyle: this.fixedStyle }}
          accept="image/*,image/heic,image/heif,.pdf"
          value={this.value}
          disabled={this.disabled}
          required={this.value ? false : this.required}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          onChange={this.onInput}
          ref={(el: HTMLInputElement) => {
            this.inputInternalRef = el;
            if (this.inputRef) {
              this.inputRef(el);
            }
          }}
        />
        <label class={{ FixedStyle: this.fixedStyle }} htmlFor={this.name}>
          <Fragment>
            <UploadIcon />
            <span>{!!this.value ? this.value : this.label}</span>
          </Fragment>
        </label>
      </Fragment>
    );
  }

  renderTextAreaInput() {
    return (
      <textarea
        id={this.name}
        name={this.name}
        placeholder={this.placeholder}
        disabled={this.disabled}
        required={this.required}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        onInput={this.onInput}
        class={{ FixedStyle: this.fixedStyle }}
        ref={(el: HTMLTextAreaElement) => {
          this.inputInternalRef = el;
          if (this.inputRef) {
            this.inputRef(el);
          }
        }}
      >
        {this.value}
      </textarea>
    );
  }

  renderInputField() {
    return (
      <input
        id={this.name}
        type={this.type}
        name={this.name}
        placeholder={this.placeholder}
        max={this.max}
        min={this.min}
        value={this.value}
        disabled={this.disabled}
        readOnly={this.readOnly}
        required={this.required}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        onInput={this.onInput}
        class={{ FixedStyle: this.fixedStyle }}
        ref={(el: HTMLInputElement) => {
          this.inputInternalRef = el;
          if (this.inputRef) {
            this.inputRef(el);
          }
        }}
      />
    );
  }

  renderLabel() {
    return (
      <label class={this.labelClass} htmlFor={this.name}>
        {this.label}
      </label>
    );
  }

  renderInput() {
    if (this.type === 'date') {
      return <Fragment>{this.renderDateInput()}</Fragment>;
    } else if (this.type === 'textarea') {
      return (
        <Fragment>
          {this.renderTextAreaInput()}
          {this.renderLabel()}
        </Fragment>
      );
    } else if (this.type === 'file') {
      return this.renderFileInput();
    } else {
      return (
        <Fragment>
          {this.renderInputField()}
          {this.renderLabel()}
        </Fragment>
      );
    }
  }

  get simpleGroup(): boolean {
    return this.inputStyle === InputStyle.Simple || this.inputStyle === InputStyle.OrderConfig;
  }

  render() {
    return (
      <div class={this.wrapperClass}>
        {this.inputStyle === InputStyle.Default && <Fragment>{this.renderInput()}</Fragment>}

        {this.simpleGroup && (
          <Fragment>
            {this.inputStyle === InputStyle.OrderConfig && <div class="ConfigLabel">{this.label}</div>}
            {rentwareIcon(this.customIconName, this.customIconStyle)}
            {this.renderInputField()}
          </Fragment>
        )}

        {(this.inputStyle === InputStyle.Config || this.inputStyle === InputStyle.NewConfig) && (
          <Fragment>
            {rentwareIcon(this.customIconName, this.customIconStyle)}

            <div class="ConfigTextContainer">
              <div class="ConfigTextContainer">
                <div class="ConfigLabel">{this.label}</div>

                {this.renderInputField()}
              </div>
            </div>
          </Fragment>
        )}
      </div>
    );
  }
}
